import { Chart, registerables } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartLayout } from './ChartLayout';
import './ChartLayout.css';

Chart.register(...registerables);

interface TransportationModeShareProps {
  location: string;
  geofips: number | null;
  onModeShareCalculated?: (modeShare: ModeShareData) => void;
  embedOnly?: boolean;
}

interface ModeData {
  mode: string;
  vmt: number;
  percentage: number;
  color: string;
}

export interface ModeShareData {
  privateAuto: number;
  publicTransit: number;
  walking: number;
  biking: number;
  carpool: number;
  onDemand: number;
  commercial: number;
  other: number;
  totalVMT: number;
}

export const TransportationModeShare: React.FC<TransportationModeShareProps> = ({ 
  location, 
  geofips, 
  onModeShareCalculated,
  embedOnly = false
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modeData, setModeData] = useState<ModeData[]>([]);

  useEffect(() => {
    const fetchModeShareData = async () => {
      if (!location || !geofips) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log(`Fetching VMT data for ${location} (geofips: ${geofips})`);
        
        // Fetch VMT data by mode for 2023
        const { data: vmtData, error: vmtError } = await supabaseClient
          .from('replica_vmt_by_mode')
          .select('mode, vmt')
          .eq('geofips', geofips)
          .eq('year', 2023)
          .order('vmt', { ascending: false });

        if (vmtError) {
          console.error('Error fetching VMT data:', vmtError);
          throw vmtError;
        }

        if (!vmtData || vmtData.length === 0) {
          setError('No transportation mode data available for this location');
          setLoading(false);
          return;
        }

        console.log('Raw VMT data:', vmtData);

        // Calculate total VMT
        const totalVMT = vmtData.reduce((sum, record) => sum + (record.vmt || 0), 0);
        console.log('Total VMT:', totalVMT);

        // Define colors for each mode
        const modeColors: { [key: string]: string } = {
          'PRIVATE_AUTO': '#dc2626',
          'CARPOOL': '#f59e0b', 
          'PUBLIC_TRANSIT': '#3b82f6',
          'WALKING': '#10b981',
          'BIKING': '#059669',
          'ON_DEMAND_AUTO': '#8b5cf6',
          'COMMERCIAL': '#6b7280',
          'OTHER_TRAVEL_MODE': '#9ca3af'
        };

        // Calculate percentages and create mode data
        const processedData: ModeData[] = vmtData
          .filter(record => record.vmt > 0) // Only include modes with VMT > 0
          .map(record => ({
            mode: record.mode,
            vmt: record.vmt,
            percentage: (record.vmt / totalVMT) * 100,
            color: modeColors[record.mode] || '#9ca3af'
          }))
          .sort((a, b) => b.percentage - a.percentage); // Sort by percentage descending

        console.log('Processed mode data:', processedData);
        setModeData(processedData);

        // Calculate mode share percentages for callback
        const modeSharePercentages = {
          privateAuto: processedData.find(d => d.mode === 'PRIVATE_AUTO')?.percentage || 0,
          publicTransit: processedData.find(d => d.mode === 'PUBLIC_TRANSIT')?.percentage || 0,
          walking: processedData.find(d => d.mode === 'WALKING')?.percentage || 0,
          biking: processedData.find(d => d.mode === 'BIKING')?.percentage || 0,
          carpool: processedData.find(d => d.mode === 'CARPOOL')?.percentage || 0,
          onDemand: processedData.find(d => d.mode === 'ON_DEMAND_AUTO')?.percentage || 0,
          commercial: processedData.find(d => d.mode === 'COMMERCIAL')?.percentage || 0,
          other: processedData.find(d => d.mode === 'OTHER_TRAVEL_MODE')?.percentage || 0,
          totalVMT: totalVMT
        };

        if (onModeShareCalculated) {
          onModeShareCalculated(modeSharePercentages);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching mode share data:', err);
        setError('Failed to load transportation mode data');
        setLoading(false);
      }
    };

    fetchModeShareData();
  }, [location, geofips, onModeShareCalculated]);

  // Create chart when data is ready
  useEffect(() => {
    if (modeData.length === 0 || loading) return;

    const createChart = () => {
      if (!chartRef.current) {
        console.error('Canvas ref is null');
        return;
      }

      const ctx = chartRef.current.getContext('2d');
      if (!ctx) {
        console.error('Could not get canvas context');
        return;
      }

      // Destroy existing chart
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      console.log('Creating doughnut chart with data:', modeData);

      chartInstance.current = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: modeData.map(d => d.mode.replace('_', ' ')),
          datasets: [{
            data: modeData.map(d => d.percentage),
            backgroundColor: modeData.map(d => d.color),
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            title: {
              display: false
            },
            legend: {
              position: 'right',
              labels: {
                usePointStyle: true,
                padding: 20,
                font: {
                  size: 12
                }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const label = context.label || '';
                  const value = context.parsed;
                  return `${label}: ${value.toFixed(1)}%`;
                }
              }
            }
          },
          cutout: '60%'
        }
      });

      console.log('Chart created successfully');
    };

    // Small delay to ensure canvas is ready
    setTimeout(createChart, 100);
  }, [modeData, loading]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Transportation_Mode_Share');
    downloadChartAsPNG(chartRef, filename);
  };

  const handleDownloadCSV = () => {
    const filename = formatFilename(location, 'Transportation_Mode_Share_Data');
    downloadDataAsCSV(modeData, filename);
  };

  if (loading) {
    return (
      <div style={{ height: '400px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading transportation mode data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '400px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#ef4444' }}>{error}</div>
      </div>
    );
  }

  const chartContent = (
    <div id="mode-share-chart" style={{ height: 400, width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  if (embedOnly) {
    return chartContent;
  }

  return (
    <ChartLayout
      title="Transportation Mode Share"
      description={location}
      chartType="bar"
      chartContent={chartContent}
      activeView="overview"
      onViewChange={() => {}}
      showRelevanceEquity={false}
      layout="side-by-side"
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={formatFilename(location, 'Transportation_Mode_Share')}
      csvFilename={formatFilename(location, 'Transportation_Mode_Share_Data')}
    />
  );
};